/**
 * Direct-won installation Actual Expenses only.
 * Normal RR→Accept→Won keeps computeInstallationManagerActualExpense.
 *
 * Actual = efforts + otherExpenses
 * otherExpenses = Σ line items + Σ per assignee: trip × (perdiem + averageCost)
 * (installation manager + installation employee each get perdiem/avg — same as Associated Costs)
 */

import { isDirectWonLead, isExplicitDirectWonLead } from "./isDirectWonLead";
import {
  getPerdiemAndAverageCostFromExpenses,
  resolveEntityId,
  scopeOtherExpensesForUser,
} from "./salespersonRatesScope";

const normalizeKey = (value) => String(value ?? "").trim().toLowerCase();

const resolveProjectId = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    return resolveEntityId(value._id ?? value.id ?? value);
  }
  return String(value).trim();
};

const isInstallationPerdiemOrAvgDesc = (desc) => {
  const key = String(desc?.description ?? desc?.Description ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return (
    key === "perdiem" ||
    key === "per diem" ||
    key === "average cost" ||
    key === "avg cost" ||
    key === "averagecost"
  );
};

const readTripDuration = (desc) =>
  Number(
    desc?.tripduration ??
      desc?.tripDuration ??
      desc?.TripDuration ??
      0
  ) || 0;

export function findLeadForQuotation(
  leads = [],
  quotationIdOrName,
  trackingProjects = []
) {
  const needle = String(quotationIdOrName ?? "").trim();
  if (!needle) return null;
  const list = Array.isArray(leads) ? leads : [];
  const needleKey = normalizeKey(needle);

  const byQuotation =
    list.find((l) => String(l?.quotationId ?? "").trim() === needle) ||
    list.find((l) => String(l?.QuotationId ?? "").trim() === needle);
  if (byQuotation) return byQuotation;

  const byName = list.find(
    (l) => normalizeKey(l?.ProjectName) === needleKey
  );
  if (byName) return byName;

  const tracking = (trackingProjects || []).find((p) => {
    const qid = String(p?.quotationId ?? p?.QuotationId ?? "").trim();
    const name = String(p?.ProjectName ?? "").trim();
    return qid === needle || normalizeKey(name) === needleKey;
  });
  const leadId = resolveEntityId(
    tracking?.leadid ?? tracking?.leadId ?? tracking?.LeadId
  );
  if (leadId) {
    return list.find((l) => resolveEntityId(l?._id) === leadId) || null;
  }
  return null;
}

export function shouldUseDirectWonInstallationActual(
  lead,
  projects = [],
  options = {}
) {
  if (isExplicitDirectWonLead(lead)) return true;
  if (lead && isDirectWonLead(lead, projects, options)) return true;

  const tracking = Array.isArray(options.trackingProjects)
    ? options.trackingProjects
    : [];
  if (
    tracking.length > 0 &&
    tracking.every(
      (p) => !p?.oldprojectid && !p?.oldProjectId && !p?.OldProjectId
    )
  ) {
    return true;
  }
  return false;
}

const resolveEmployeeIdByEmail = (email, employees = []) => {
  const key = normalizeKey(email);
  if (!key) return "";
  const emp = employees.find((e) => normalizeKey(e?.EmpEmail) === key);
  return resolveEntityId(emp?._id);
};

/** Shared perdiem / average cost (salesperson Settings — same for all assignees). */
export function resolveDirectWonPerdiemAndAvgCost({
  projectRow,
  leadRow = null,
  employees = [],
  otherExpenses = [],
  ratesSource = null,
  fallbackRates = null,
  managerId = "",
} = {}) {
  const source = ratesSource || projectRow;
  const frozenP = Number(source?.perdiem) || 0;
  const frozenA = Number(source?.avgcost ?? source?.averagecost) || 0;
  if (frozenP || frozenA) {
    return { perdiem: frozenP, averagecost: frozenA };
  }

  const list = Array.isArray(otherExpenses) ? otherExpenses : [];
  const readScoped = (ownerId) => {
    const id = resolveEntityId(ownerId);
    if (!id) return null;
    const scoped = scopeOtherExpensesForUser(list, { loggedUserId: id });
    const rates = getPerdiemAndAverageCostFromExpenses(scoped);
    const p = Number(rates?.perdiem) || 0;
    const a = Number(rates?.averagecost) || 0;
    return p || a ? { perdiem: p, averagecost: a } : null;
  };

  const salespersonId =
    resolveEntityId(projectRow?.SalesPersonId ?? leadRow?.SalesPersonId) ||
    resolveEntityId(leadRow?.createdBy) ||
    resolveEmployeeIdByEmail(
      projectRow?.SalesPersonEmail ??
        leadRow?.SalesPersonEmail ??
        leadRow?.LeadOwnerEmail,
      employees
    );

  const fromSales = readScoped(salespersonId);
  if (fromSales) return fromSales;

  const fromManager = readScoped(managerId || projectRow?.managerid);
  if (fromManager) return fromManager;

  const fbP = Number(fallbackRates?.perdiem) || 0;
  const fbA = Number(fallbackRates?.averagecost) || 0;
  if (fbP || fbA) {
    return { perdiem: fbP, averagecost: fbA };
  }

  return { perdiem: 0, averagecost: 0 };
}

function maxTripOnProject(projectIds, descriptionLists) {
  const idSet = new Set(
    (projectIds || []).map((id) => resolveProjectId(id)).filter(Boolean)
  );
  let maxTrip = 0;
  (descriptionLists || []).forEach((list) => {
    (list || []).forEach((desc) => {
      const pid = resolveProjectId(desc?.projectid);
      if (idSet.size > 0 && pid && !idSet.has(pid)) return;
      maxTrip = Math.max(maxTrip, readTripDuration(desc));
    });
  });
  return maxTrip;
}

/** Trip for one assignee: own max, else project max (manager inherits employee trip). */
function tripForAssignee(personId, allDescriptions, projectMaxTrip) {
  let own = 0;
  (allDescriptions || []).forEach((desc) => {
    const owner =
      resolveEntityId(desc?.taskownerid) || resolveEntityId(desc?.createdBy);
    if (owner !== personId) return;
    own = Math.max(own, readTripDuration(desc));
  });
  if (own > 0) return own;
  return projectMaxTrip > 0 ? projectMaxTrip : 0;
}

/** Installation manager + employee assignees on this project. */
function collectInstallationAssignees({
  taskList,
  descriptionList,
  trackingDescriptions,
  trackingProjectRaw,
  mgrId,
}) {
  const ordered = [];
  const push = (id) => {
    const resolved = resolveEntityId(id);
    if (resolved && !ordered.includes(resolved)) ordered.push(resolved);
  };

  push(mgrId);
  (trackingProjectRaw?.Assignedperson || []).forEach((p) => {
    push(p?.personid ?? p?.personId ?? p?._id);
  });

  [...taskList, ...descriptionList, ...trackingDescriptions].forEach((row) => {
    push(row?.taskownerid);
    push(row?.createdBy);
  });

  return ordered;
}

function assigneeHasProjectActivity(personId, taskList, allDescriptions) {
  const pid = resolveEntityId(personId);
  if (!pid) return false;
  if ((taskList || []).some((t) => resolveEntityId(t?.taskownerid) === pid)) {
    return true;
  }
  return (allDescriptions || []).some(
    (d) => resolveEntityId(d?.taskownerid) === pid
  );
}

/**
 * Direct-won Actual:
 *   efforts + lineOther + Σ_assignees trip_i × (perdiem + averageCost)
 */
export function computeDirectWonInstallationActualExpense(
  projectExpenseRow,
  ownedTracking,
  managerId,
  quotations,
  allProjects = [],
  otherExpenses = [],
  {
    lead = null,
    employees = [],
    resolveEffortRate,
    resolveProjectRatesSource,
    allDescriptionTasks = [],
    fallbackRates = null,
  } = {}
) {
  if (!projectExpenseRow) return 0;
  if (typeof resolveEffortRate !== "function") return 0;
  if (typeof resolveProjectRatesSource !== "function") return 0;

  const installationTracking = (ownedTracking || []).filter((p) =>
    String(p.TypeService || "").toLowerCase().includes("installation")
  );
  const scopedTracking =
    installationTracking.length > 0 ? installationTracking : ownedTracking || [];
  const projectIds = scopedTracking.map((p) => p._id).filter(Boolean);
  const installationProjectIds = new Set(
    projectIds.map((id) => resolveProjectId(id)).filter(Boolean)
  );

  const trackingProjectRaw = scopedTracking[0] || null;
  const trackingProject =
    resolveProjectRatesSource(trackingProjectRaw, allProjects) ||
    trackingProjectRaw;
  const mgrId =
    String(managerId || "").trim() ||
    resolveEntityId(trackingProject?.managerid || trackingProject?.managerId);

  const isInstallationScopedRow = (row) => {
    if (!row) return false;
    const projectId = resolveProjectId(row.projectid);
    if (projectId) {
      return (
        installationProjectIds.size === 0 ||
        installationProjectIds.has(projectId)
      );
    }
    if (
      String(row.typeservice || row.TypeService || "")
        .toLowerCase()
        .includes("installation")
    ) {
      return true;
    }
    return installationProjectIds.size === 0;
  };

  const trackingDescriptions = (allDescriptionTasks || []).filter((d) => {
    const pid = resolveProjectId(d?.projectid);
    return pid && installationProjectIds.has(pid);
  });

  const taskList = (projectExpenseRow.TaskList || []).filter(
    isInstallationScopedRow
  );
  let descriptionList = (projectExpenseRow.Description || []).filter(
    isInstallationScopedRow
  );
  if (descriptionList.length === 0 && (projectExpenseRow.Description || []).length) {
    descriptionList = projectExpenseRow.Description || [];
  }

  const allDescriptions = [...descriptionList, ...trackingDescriptions];

  let effort = 0;
  taskList.forEach((task) => {
    const hours = Number(task.hoursspent) || 0;
    const taskProject =
      scopedTracking.find(
        (p) => resolveProjectId(p._id) === resolveProjectId(task.projectid)
      ) || trackingProjectRaw;
    const ratesProject =
      resolveProjectRatesSource(taskProject, allProjects) || trackingProject;
    effort += hours * resolveEffortRate(task, ratesProject, mgrId, quotations);
  });

  let lineOther = 0;
  descriptionList.forEach((desc) => {
    if (isInstallationPerdiemOrAvgDesc(desc)) return;
    lineOther += Number(desc.expenses) || Number(desc.value) || 0;
  });

  const source = resolveProjectRatesSource(trackingProjectRaw, allProjects);
  const { perdiem, averagecost } = resolveDirectWonPerdiemAndAvgCost({
    projectRow: trackingProjectRaw,
    leadRow: lead,
    employees,
    otherExpenses,
    ratesSource: source,
    fallbackRates,
    managerId: mgrId,
  });

  const rateSum = (Number(perdiem) || 0) + (Number(averagecost) || 0);
  const projectMaxTrip = maxTripOnProject(projectIds, [
    descriptionList,
    trackingDescriptions,
  ]);

  const assignees = collectInstallationAssignees({
    taskList,
    descriptionList,
    trackingDescriptions,
    trackingProjectRaw,
    mgrId,
  });

  let tripAddon = 0;
  assignees.forEach((personId) => {
    const hasActivity = assigneeHasProjectActivity(
      personId,
      taskList,
      allDescriptions
    );
    // Include manager even when only employee submitted descriptions (inherit trip).
    const isManager = personId === mgrId;
    if (!hasActivity && !isManager) return;

    let trip = tripForAssignee(personId, allDescriptions, projectMaxTrip);
    if (trip <= 0 && rateSum > 0 && (hasActivity || isManager)) {
      trip = projectMaxTrip > 0 ? projectMaxTrip : 1;
    }
    if (trip <= 0 || rateSum <= 0) return;

    tripAddon +=
      trip * (Number(perdiem) || 0) + trip * (Number(averagecost) || 0);
  });

  // Fallback: at least one project-level add-on when rates exist and rows submitted.
  if (
    tripAddon === 0 &&
    rateSum > 0 &&
    (descriptionList.length > 0 || taskList.length > 0)
  ) {
    const trip = projectMaxTrip > 0 ? projectMaxTrip : 1;
    tripAddon = trip * (Number(perdiem) || 0) + trip * (Number(averagecost) || 0);
  }

  return effort + lineOther + tripAddon;
}
